// Tribunais do país e os calendários oficiais já conferidos.
// Calendário só entra aqui copiado do ato do próprio tribunal: um feriado a mais faria o prazo vencer depois do correto.

export type CourtGroup = 'Justiça Estadual' | 'Justiça Federal' | 'Justiça do Trabalho' | 'Justiça Eleitoral' | 'Justiça Militar' | 'Tribunais Superiores';

export interface CourtOption {
  code: string;
  name: string;
  group: CourtGroup;
}

const STATES: [string, string][] = [
  ['AC', 'Acre'], ['AL', 'Alagoas'], ['AP', 'Amapá'], ['AM', 'Amazonas'], ['BA', 'Bahia'], ['CE', 'Ceará'], ['DF', 'Distrito Federal e Territórios'],
  ['ES', 'Espírito Santo'], ['GO', 'Goiás'], ['MA', 'Maranhão'], ['MT', 'Mato Grosso'], ['MS', 'Mato Grosso do Sul'], ['MG', 'Minas Gerais'], ['PA', 'Pará'],
  ['PB', 'Paraíba'], ['PR', 'Paraná'], ['PE', 'Pernambuco'], ['PI', 'Piauí'], ['RJ', 'Rio de Janeiro'], ['RN', 'Rio Grande do Norte'], ['RS', 'Rio Grande do Sul'],
  ['RO', 'Rondônia'], ['RR', 'Roraima'], ['SC', 'Santa Catarina'], ['SP', 'São Paulo'], ['SE', 'Sergipe'], ['TO', 'Tocantins'],
];

const TRT_REGIONS: Record<number, string> = {
  1: 'RJ', 2: 'SP, capital e região', 3: 'MG', 4: 'RS', 5: 'BA', 6: 'PE', 7: 'CE', 8: 'PA e AP', 9: 'PR', 10: 'DF e TO', 11: 'AM e RR', 12: 'SC',
  13: 'PB', 14: 'RO e AC', 15: 'Campinas/SP', 16: 'MA', 17: 'ES', 18: 'GO', 19: 'AL', 20: 'SE', 21: 'RN', 22: 'PI', 23: 'MT', 24: 'MS',
};

const TRF_REGIONS: Record<number, string> = {
  1: 'DF, GO, TO, MT, BA, PI, MA, PA, AM, AC, RR, RO, AP',
  2: 'RJ e ES',
  3: 'SP e MS',
  4: 'RS, PR e SC',
  5: 'PE, AL, CE, PB, RN e SE',
  6: 'MG',
};

export const COURTS: CourtOption[] = [
  ...STATES.map(([uf, name]) => ({ code: uf === 'DF' ? 'TJDFT' : `TJ${uf}`, name, group: 'Justiça Estadual' as const })),
  ...Object.entries(TRF_REGIONS).map(([n, area]) => ({ code: `TRF${n}`, name: area, group: 'Justiça Federal' as const })),
  ...Object.entries(TRT_REGIONS).map(([n, area]) => ({ code: `TRT${n}`, name: area, group: 'Justiça do Trabalho' as const })),
  ...STATES.map(([uf, name]) => ({ code: `TRE-${uf}`, name, group: 'Justiça Eleitoral' as const })),
  { code: 'STM', name: 'Superior Tribunal Militar', group: 'Justiça Militar' },
  { code: 'TJMMG', name: 'Minas Gerais', group: 'Justiça Militar' },
  { code: 'TJMRS', name: 'Rio Grande do Sul', group: 'Justiça Militar' },
  { code: 'TJMSP', name: 'São Paulo', group: 'Justiça Militar' },
  { code: 'STF', name: 'Supremo Tribunal Federal', group: 'Tribunais Superiores' },
  { code: 'STJ', name: 'Superior Tribunal de Justiça', group: 'Tribunais Superiores' },
  { code: 'TST', name: 'Tribunal Superior do Trabalho', group: 'Tribunais Superiores' },
  { code: 'TSE', name: 'Tribunal Superior Eleitoral', group: 'Tribunais Superiores' },
];

export const COURT_GROUPS: CourtGroup[] = ['Justiça Estadual', 'Justiça Federal', 'Justiça do Trabalho', 'Justiça Eleitoral', 'Justiça Militar', 'Tribunais Superiores'];

export const courtGroupOf = (code: string) => COURTS.find((c) => c.code === code)?.group;

interface CalendarDay {
  date: string;
  reason: string;
  recifeOnly?: boolean;
}

interface OfficialCalendar {
  source: string;
  days: CalendarDay[];
}

const range = (year: number, month: number, from: number, to: number, reason: string): CalendarDay[] =>
  Array.from({ length: to - from + 1 }, (_, i) => ({
    date: `${year}-${String(month).padStart(2, '0')}-${String(from + i).padStart(2, '0')}`,
    reason,
  }));
const on = (year: number, month: number, day: number, reason: string, recifeOnly = false): CalendarDay => ({
  ...range(year, month, day, day, reason)[0],
  recifeOnly,
});

const OFFICIAL: Record<string, Record<number, OfficialCalendar>> = {
  TJPE: {
    2026: {
      source: 'Ato Conjunto 43/2025 do TJPE',
      days: [
        on(2026, 1, 1, 'Confraternização Universal'),
        ...range(2026, 1, 2, 6, 'Sem expediente (COJE, art. 94)'),
        on(2026, 2, 16, 'Carnaval'), on(2026, 2, 17, 'Carnaval'), on(2026, 2, 18, 'Cinzas'),
        on(2026, 3, 6, 'Data Magna de Pernambuco'),
        on(2026, 4, 2, 'Semana Santa'), on(2026, 4, 3, 'Semana Santa'), on(2026, 4, 5, 'Páscoa'),
        on(2026, 4, 21, 'Tiradentes'), on(2026, 5, 1, 'Dia do Trabalho'),
        on(2026, 6, 22, 'Corpus Christi (transferido de 04/06)'),
        on(2026, 6, 23, 'Sem expediente (COJE, art. 94)'),
        on(2026, 6, 24, 'São João'),
        ...range(2026, 6, 25, 30, 'Sem expediente (COJE, art. 94)'),
        on(2026, 7, 16, 'Nossa Senhora do Carmo (Comarca do Recife)', true),
        on(2026, 8, 10, 'Dia dos Cursos Jurídicos (antecipado de 11/08)'),
        on(2026, 9, 7, 'Independência'), on(2026, 10, 12, 'Nossa Senhora Aparecida'),
        on(2026, 10, 30, 'Dia do Servidor Público (transferido de 28/10)'),
        on(2026, 11, 2, 'Finados'), on(2026, 11, 15, 'Proclamação da República'), on(2026, 11, 20, 'Consciência Negra'),
        on(2026, 12, 8, 'Nossa Senhora da Conceição e Dia da Justiça'),
        ...range(2026, 12, 20, 24, 'Sem expediente (COJE, art. 94)'),
        on(2026, 12, 25, 'Natal'),
        ...range(2026, 12, 26, 31, 'Sem expediente (COJE, art. 94)'),
      ],
    },
    2027: {
      source: 'Ato 1502/2026 do TJPE',
      days: [
        on(2027, 1, 1, 'Confraternização Universal'),
        ...range(2027, 1, 2, 6, 'Sem expediente (COJE, art. 94)'),
        on(2027, 2, 8, 'Carnaval'), on(2027, 2, 9, 'Carnaval'), on(2027, 2, 10, 'Cinzas'),
        on(2027, 3, 6, 'Data Magna de Pernambuco'),
        on(2027, 3, 25, 'Quinta-feira Santa'), on(2027, 3, 26, 'Sexta-feira Santa'), on(2027, 3, 28, 'Páscoa'),
        on(2027, 4, 21, 'Tiradentes'), on(2027, 5, 1, 'Dia do Trabalho'),
        on(2027, 5, 28, 'Corpus Christi (transferido de 27/05)'),
        on(2027, 6, 23, 'Sem expediente (COJE, art. 94)'),
        on(2027, 6, 24, 'São João'),
        ...range(2027, 6, 25, 30, 'Sem expediente (COJE, art. 94)'),
        on(2027, 7, 16, 'Nossa Senhora do Carmo (Comarca do Recife)', true),
        on(2027, 8, 9, 'Dia dos Cursos Jurídicos (antecipado de 11/08)'),
        on(2027, 9, 7, 'Independência'), on(2027, 10, 12, 'Nossa Senhora Aparecida'),
        on(2027, 10, 29, 'Dia do Servidor Público (transferido de 28/10)'),
        on(2027, 11, 1, 'Sem expediente'), on(2027, 11, 2, 'Finados'),
        on(2027, 11, 15, 'Proclamação da República'), on(2027, 11, 20, 'Consciência Negra'),
        on(2027, 12, 8, 'Nossa Senhora da Conceição e Dia da Justiça'),
        ...range(2027, 12, 20, 24, 'Sem expediente (COJE, art. 94)'),
        on(2027, 12, 25, 'Natal'),
        ...range(2027, 12, 26, 31, 'Sem expediente (COJE, art. 94)'),
      ],
    },
  },
  TRF5: {
    2026: {
      source: 'Ato 219/2026 do TRF5',
      days: [
        ...range(2026, 1, 1, 6, 'Recesso judiciário'),
        on(2026, 2, 16, 'Carnaval'), on(2026, 2, 17, 'Carnaval'), on(2026, 2, 18, 'Cinzas (ponto facultativo)'),
        on(2026, 3, 6, 'Data Magna de Pernambuco'),
        ...range(2026, 4, 1, 5, 'Semana Santa'),
        on(2026, 4, 21, 'Tiradentes'), on(2026, 5, 1, 'Dia do Trabalho'),
        on(2026, 6, 4, 'Corpus Christi (ponto facultativo)'),
        on(2026, 6, 23, 'Véspera de São João (ponto facultativo)'),
        on(2026, 6, 24, 'São João'),
        on(2026, 7, 16, 'Nossa Senhora do Carmo (Recife)', true),
        on(2026, 8, 11, 'Criação dos Cursos Jurídicos'),
        on(2026, 9, 7, 'Independência'), on(2026, 10, 12, 'Nossa Senhora Aparecida'),
        on(2026, 10, 28, 'Dia do Servidor Público (ponto facultativo)'),
        on(2026, 11, 1, 'Todos os Santos'), on(2026, 11, 2, 'Finados'),
        on(2026, 11, 15, 'Proclamação da República'), on(2026, 11, 20, 'Consciência Negra'),
        on(2026, 12, 8, 'Dia da Justiça'),
        ...range(2026, 12, 20, 31, 'Recesso forense'),
      ],
    },
  },
  TRT6: {
    2026: {
      source: 'Portaria TRT6-GP 495/2025',
      days: [
        ...range(2026, 1, 1, 6, 'Recesso forense'),
        on(2026, 2, 16, 'Carnaval'), on(2026, 2, 17, 'Carnaval'), on(2026, 2, 18, 'Cinzas'),
        on(2026, 3, 6, 'Data Magna de Pernambuco'),
        ...range(2026, 4, 1, 3, 'Semana Santa'),
        on(2026, 4, 21, 'Tiradentes'), on(2026, 5, 1, 'Dia do Trabalho'),
        on(2026, 6, 23, 'Corpus Christi (adiado)'),
        on(2026, 6, 24, 'São João'),
        on(2026, 8, 11, 'Criação dos Cursos Jurídicos'),
        on(2026, 9, 7, 'Independência'), on(2026, 10, 12, 'Nossa Senhora Aparecida'),
        on(2026, 10, 30, 'Dia do Servidor Público (adiado)'),
        on(2026, 11, 2, 'Finados'), on(2026, 11, 20, 'Consciência Negra'),
        on(2026, 12, 8, 'Dia da Justiça'),
        ...range(2026, 12, 21, 31, 'Recesso forense'),
      ],
    },
  },
};

export const officialCalendar = (court: string, year: number) => OFFICIAL[court]?.[year];
export const hasRecifeDays = (court: string) => Object.values(OFFICIAL[court] || {}).some((c) => c.days.some((d) => d.recifeOnly));
